import { useHumanHandoverStore } from '@/stores/humanHandover/humanHandoverStore';
import { useEffect, useRef } from 'react';
import { getChatFileSettings } from '@/services/humanHandover';

/**
 * Hook to determine if file upload is allowed based on context and settings
 *
 * @param {boolean} isInHumanHandoverApp - Whether we're in the agent dashboard
 * @param {boolean} pollResponse - Whether an agent has joined (user side)
 * @param {string} token - User token (for user side API calls)
 * @param {string} conversationId - Current conversation ID
 * @returns {boolean} Whether file upload should be allowed
 */
const useFileUploadPermission = (isInHumanHandoverApp, pollResponse, token = null, conversationId = null) => {
  const { fileUploadSetting, explicitlyEnabledFileUpload, setFileUploadSetting } = useHumanHandoverStore();
  const checkIntervalRef = useRef(null);

  // Helper function to extract boolean value from setting
  const getSettingValue = (setting) => {
    if (setting === null || setting === undefined) {
      return false;
    }
    return typeof setting === 'boolean' ? setting : setting?.allow_file_upload;
  };

  // For user side: periodically check if agent has enabled file upload
  useEffect(() => {
    // Only for user side when agent has joined
    if (!isInHumanHandoverApp && pollResponse && token && conversationId) {
      // Check immediately
      const checkFileUploadSetting = async () => {
        try {
          const response = await getChatFileSettings(token, conversationId);

          // Handle different response structures
          let isEnabled = false;
          if (typeof response.data === 'boolean') {
            isEnabled = response.data;
          } else if (response.data?.allow_file_upload !== undefined) {
            isEnabled = response.data.allow_file_upload;
          } else {
            isEnabled = false;
          }

          // Only update store if the value has changed
          if (getSettingValue(fileUploadSetting) !== isEnabled) {
            setFileUploadSetting(isEnabled);
          }
        } catch (error) {
          // If API fails, don't change the current setting
          console.warn('Failed to check file upload setting:', error);
        }
      };

      // Check immediately
      checkFileUploadSetting();

      // Then check every 10 seconds (less frequent than before)
      checkIntervalRef.current = setInterval(checkFileUploadSetting, 10000);

      return () => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      };
    }

    // Clean up interval if conditions change
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isInHumanHandoverApp, pollResponse, token, conversationId, fileUploadSetting, setFileUploadSetting]);

  // For agent side (human handover app)
  if (isInHumanHandoverApp) {
    return explicitlyEnabledFileUpload && getSettingValue(fileUploadSetting);
  }

  // For user side (shared chat bubble)
  // Show file upload button based on the current setting
  if (pollResponse) {
    return getSettingValue(fileUploadSetting);
  }

  // Hide button until agent joins
  return false;
};

export default useFileUploadPermission;
